import HttpService from "./HttpService";
import Logger from "../Logger/Logger";
import {SERVER_URL} from "../../Config/Parameters";
import PowerClient from "../Models/PowerClient";
import {powerClientRepository} from "../Repositories/PowerClientRepository";
import {ErrorCode, ThirtyOneError} from "../Error/ThirtyOneError";
import SampleData from "../SampleData/SampleData.json";
import ConnectionStatus from "../Models/ConnectionStatus";
import HttpResult from "../Models/HttpResult";
import {EXPIRED_TOKEN} from "../../Config/Translations";
import {userRepository} from "../Repositories/UserRepository";

const logger = Logger.Create('PowerClientService');

/**
 * The Power Client Service.
 */
export default class PowerClientService
{
    /**
     * Requests the server for Power Clients.
     * @param retrieveDummyData Set true to get the data in the Sample Data folder.
     */
    public static async getClients(retrieveDummyData = false): Promise<boolean>
    {
        if (retrieveDummyData)
        {
            const sampleDataString: string = JSON.stringify(SampleData);
            const sampleDataObject: PowerClient[] = JSON.parse(sampleDataString);

            powerClientRepository.updatePowerClients(sampleDataObject);
            return true;
        }
        
        const url = `${SERVER_URL}/PowerClient/`;
        
        logger.info(`Requesting the server for all power clients...`);
        
        let success = false;
        
        await HttpService.tokenlessGet<PowerClient[]>(url)
            .then((powerClients): void =>
            {
                powerClientRepository.updatePowerClients(powerClients);
                
                this.getConnectionStatus();
                success = true;
            })
            .catch((error: ThirtyOneError) =>
            {
                logger.error(`Request failed with error: ${error.message}`);
                success = false;
            })
        
        return success;
    }

    /**
     * Retrieves the connection status from the server.
     */
    public static async getConnectionStatus(): Promise<void>
    {
        const url = `${SERVER_URL}/PowerClient/ConnectionStatus`;

        logger.info(`Requesting the server all power client connection statuses...`);

        await HttpService.tokenlessGet<ConnectionStatus[]>(url)
            .then((connectionStatus): void =>
            {
                powerClientRepository.updateConnectionStatuses(connectionStatus);
            })
            .catch((error: ThirtyOneError) =>
            {
                logger.error(`Client connection status request failed with error: ${error.message}`);
            })
    }

    /**
     * Makes a request to the server to update the client name.
     */
    public static async updateClientName(powerClient: PowerClient, name: string): Promise<HttpResult>
    {
        const result: HttpResult = {
            success: true,
            message: 'Client name updated.',
        }
        
        const url = `${SERVER_URL}/powerclient`;
        
        const body = {
            "id": powerClient.id,
            name,
            "powerProxyId": (powerClient.powerProxy === undefined || powerClient.powerProxy === null) ? null : powerClient.powerProxy.id,
        }

        logger.info(`Requesting the server to change ${powerClient.id} to ${name}. Body: ${JSON.stringify(body)}`);
        
        await HttpService.put<void>(url, body)
            .catch((error: ThirtyOneError) =>
            {
                result.success = false;
                
                if (error.errorCode !== ErrorCode.ExpiredToken)
                {
                    logger.error(`Request to change the client name failed with error: ${error.message}`);
                    result.message = `Failed to change the client name due to the following error: ${error.message}`;
                    return;
                }

                result.message = EXPIRED_TOKEN;
                userRepository.removeUserDetails();
            })
        
        return result;
    }

    /**
     * Makes a request to the server to update the client proxy.
     */
    public static async updateClientProxy(powerClient: PowerClient, proxyId: string, clientName: string | null = null): Promise<HttpResult>
    {
        const result: HttpResult = {
            success: true,
            message: 'Client proxy updated.',
        }
        
        const url = `${SERVER_URL}/PowerClient`;
        
        const body = {
            "id": powerClient.id,
            "name": clientName === null ? powerClient.name : clientName,
            "powerProxyId": proxyId.trim() === "" ? null : proxyId,
        }

        logger.info(`Requesting the server to change ${powerClient.id} proxy to ${proxyId}. Body: ${JSON.stringify(body)}`);

        await HttpService.put<void>(url, body)
            .catch((error: ThirtyOneError) =>
            {
                result.success = false;

                if (error.errorCode !== ErrorCode.ExpiredToken)
                {
                    logger.error(`Request to change the client proxy failed with error: ${error.message}`);
                    result.message = `Failed to change the proxy due to the following error: ${error.message}`;
                    return;
                }

                result.message = EXPIRED_TOKEN;
                userRepository.removeUserDetails();
            })
        
        return result;
    }
}